import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPosts } from "@/lib/api";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import NewsletterSignup from "@/components/site/NewsletterSignup";
import { Reveal, Overline } from "@/components/site/Reveal";
import { ArrowUpRight, Loader2 } from "lucide-react";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Blog() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    getPosts()
      .then(setPosts)
      .catch(() => setPosts([]));
  }, []);

  return (
    <div data-testid="blog-page">
      <Nav />
      <main className="pt-36 pb-24 md:pt-44">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal>
            <Overline>The Journal</Overline>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 font-serif text-5xl md:text-7xl font-light leading-[0.95] tracking-tight">
              Notes on leadership,
              <br />
              learning &amp; <span className="italic text-primary">life.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-xl text-lg text-muted-foreground leading-relaxed">
              Reflections on behavioral science, building a business, raising a
              family, and designing environments where everyone can flourish.
            </p>
          </Reveal>

          {posts === null ? (
            <div className="mt-20 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" strokeWidth={1.2} />
            </div>
          ) : posts.length === 0 ? (
            <p className="mt-20 text-muted-foreground">
              New writing is on the way — check back soon.
            </p>
          ) : (
            <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={0.04 * (i % 3)}>
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-card-${p.slug}`}
                    className="group flex flex-col h-full overflow-hidden rounded-3xl border border-border bg-background transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(44,42,38,0.15)]"
                  >
                    <div className="h-52 overflow-hidden bg-muted">
                      {p.cover_image ? (
                        <img
                          src={p.cover_image}
                          alt=""
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="h-full w-full bg-secondary/50" />
                      )}
                    </div>
                    <div className="flex flex-col flex-1 p-7">
                      <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        {formatDate(p.created_at)}
                      </span>
                      <h2 className="mt-3 font-serif text-2xl font-light leading-tight">
                        {p.title}
                      </h2>
                      <p className="mt-3 text-muted-foreground leading-relaxed flex-1">
                        {p.excerpt}
                      </p>
                      <span className="mt-5 inline-flex items-center gap-1 text-primary text-sm uppercase tracking-[0.16em]">
                        Read
                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>

        <div className="mx-auto max-w-[1400px] px-6 md:px-10 mt-24">
          <NewsletterSignup />
        </div>
      </main>
      <Footer />
    </div>
  );
}
